import React, { useState, useEffect } from "react";
import {
  Modal,
  Row,
  Col,
  Typography,
  Card,
  Tag,
  Spin,
  message,
  Space,
} from "antd";
import {
  ClockCircleOutlined,
  CalendarOutlined,
  ReloadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button } from "@voicecare.ai/ui-library";
import { useTheme } from "../../../ThemeContext";
import { getSlotAvailability } from "../../../utils/apiClient";
import moment from "moment";
import { Tooltip } from "@mui/material";

const { Title, Text } = Typography;

export const SlotSelectionPopup = ({
  visible,
  onClose,
  callRequestId,
  onScheduleCall,
  selectedDate,
  onBackToDateSelection,
}) => {
  const { currentTheme, themeMode } = useTheme();
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({});

  // Get user's timezone and use the selected date
  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const getSelectedDate = () => {
    return selectedDate || moment().format("MM/DD/YYYY");
  };

  // Fetch available slots when modal opens
  useEffect(() => {
    if (visible && selectedDate) {
      fetchSlots();
    }
  }, [visible, selectedDate]);

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const providerTimezone = getUserTimezone();
      const localDate = getSelectedDate();

      console.log("Fetching slots with:", { providerTimezone, localDate });

      const response = await getSlotAvailability({
        providerTimezone,
        localDate,
      });

      if (response.statusCode === "SUCCESS") {
        setSlots(response.response.slots || []);
        setBusinessInfo({
          businessHours: response.response.business_hours,
          isBusinessDay: response.response.is_business_day,
          requestedDate: response.response.requested_date,
          requestedTimezone: response.response.requested_timezone,
          totalCapacity: response.response.total_capacity,
        });
      } else {
        message.error("Failed to fetch available slots");
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      message.error("Error fetching available slots. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleScheduleCall = () => {
    if (!selectedSlot) {
      message.warning("Please select a time slot");
      return;
    }

    const requestBody = {
      utcStartTime: selectedSlot.utcStartTime,
      callRequestId: callRequestId,
    };

    console.log("Schedule call request body:", requestBody);

    message.success("Scheduling call...");
    // Call the parent callback with the request data
    onScheduleCall(requestBody);

    // Close the modal
    onClose();

    // Reset state
    setSelectedSlot(null);
    setSlots([]);
  };

  const handleClose = () => {
    setSelectedSlot(null);
    setSlots([]);
    onClose();
  };

  const handleBackToDate = () => {
    if (onBackToDateSelection) {
      setSelectedSlot(null);
      setSlots([]);
      onBackToDateSelection();
    }
  };

  const getSlotDisplayTime = (displayTime) => {
    // Extract only the start time from "09:00 AM - 10:00 AM"
    return displayTime.split(" - ")[0];
  };

  const getCapacityColor = (availableCapacity, totalCapacity) => {
    const percentage = (availableCapacity / totalCapacity) * 100;
    if (percentage >= 80) return "#52c41a"; // Green
    if (percentage >= 50) return "#faad14"; // Orange
    return "#ff4d4f"; // Red
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span
            style={{
              color: currentTheme.callhistory.text,
              fontSize: 20,
              fontWeight: 600,
            }}
          >
            Reschedule Call - Select Time Slot
          </span>
          {selectedDate && (
            <span
              style={{
                fontSize: "12px",
                color: currentTheme.callhistory.textLight,
              }}
            >
              ({moment(selectedDate, "MM/DD/YYYY").format("MMM DD, YYYY")})
            </span>
          )}
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={null}
      styles={{
        header: {
          backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
          color: currentTheme.callhistory.text,
          padding: "24px 24px 0 24px",
        },
        body: {
          backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
          padding: "32px",
        },
        content: {
          backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow:
            themeMode === "dark"
              ? "0 20px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.1)"
              : "0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
        },
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        },
      }}
    >
      <div style={{ color: currentTheme.callhistory.text }}>
        {/* Business Hours Info */}
        {businessInfo.businessHours && (
          <Card
            style={{
              backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
              marginBottom: "20px",
              borderRadius: "8px",
              border: "none",
            }}
            bodyStyle={{
              padding: "16px",
              backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
              border: "none",
            }}
            
          >
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <CalendarOutlined
                  style={{ color: currentTheme.loginPage.loginButtonColor }}
                />
                <Text
                  style={{
                    color: currentTheme.callhistory.text,
                    fontWeight: "bold",
                  }}
                >
                  {businessInfo.requestedDate} ({businessInfo.requestedTimezone}
                  )
                </Text>
                <Tag color={businessInfo.isBusinessDay ? "green" : "red"}>
                  {businessInfo.isBusinessDay
                    ? "Business Day"
                    : "Non-Business Day"}
                </Tag>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <ClockCircleOutlined
                  style={{ color: currentTheme.loginPage.loginButtonColor }}
                />
                <Text style={{ color: currentTheme.callhistory.textLight }}>
                  Business Hours: {businessInfo.businessHours}
                </Text>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <UserOutlined
                  style={{ color: currentTheme.loginPage.loginButtonColor }}
                />
                <Text style={{ color: currentTheme.callhistory.textLight }}>
                  Total Capacity: {businessInfo.totalCapacity} calls
                </Text>
              </div>
            </Space>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>
              <Text style={{ color: currentTheme.callhistory.textLight }}>
                Loading available time slots...
              </Text>
            </div>
          </div>
        )}

        {/* Slots Grid */}
        {!loading && slots.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <Title
                level={4}
                style={{
                  color: currentTheme.callhistory.text,
                  marginBottom: 0,
                }}
              >
                Available Time Slots
              </Title>
              <Tooltip title="Refresh Slots">
                <ReloadOutlined
                  onClick={fetchSlots}
                  style={{ color: currentTheme.loginPage.loginButtonColor }}
                />
              </Tooltip>
            </div>

            <Row gutter={[12, 12]} style={{ marginBottom: "24px" }}>
              {slots.map((slot) => (
                <Col xs={12} sm={8} md={6} key={slot.slotId}>
                  <Card
                    hoverable
                    onClick={() => handleSlotSelect(slot)}
                    style={{
                      backgroundColor:
                        selectedSlot?.slotId === slot.slotId
                          ? currentTheme.loginPage.loginButtonColor
                          : themeMode === "dark"
                          ? "#302d4c"
                          : "#e9e8ea",
                      border: "none",
                      borderRadius: "8px",
                      cursor: slot.isAvailable ? "pointer" : "not-allowed",
                      opacity: slot.isAvailable ? 1 : 0.5,
                      transition: "all 0.3s ease",
                    }}
                    bodyStyle={{
                      padding: "20px 12px",
                      textAlign: "center",
                      backgroundColor: "transparent",
                    }}
                    disabled={!slot.isAvailable}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      
                      <Text
                        style={{
                          color:
                            selectedSlot?.slotId === slot.slotId
                              ? "#ffffff"
                              : currentTheme.callhistory.text,
                          fontWeight: "bold",
                          fontSize: "14px",
                        }}
                      >
                        {getSlotDisplayTime(slot.displayTime)}
                      </Text>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* No Slots Available */}
        {!loading && slots.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <ClockCircleOutlined
              style={{
                fontSize: "48px",
                color: currentTheme.callhistory.textLight,
                marginBottom: "16px",
              }}
            />
            <div>
              <Text
                style={{
                  color: currentTheme.callhistory.text,
                  fontSize: "16px",
                }}
              >
                No available time slots for today
              </Text>
            </div>
            <div style={{ marginTop: "8px" }}>
              <Text style={{ color: currentTheme.callhistory.textLight }}>
                Please try again later or contact support
              </Text>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: `1px solid ${currentTheme.callhistory.borderColor}`,
          }}
        >
          <div style={{ display: "flex", gap: "12px" }}>
            {/* Back to Date Selection Button */}
            {onBackToDateSelection && (
              <Button
                label="← Back to Date"
                onClick={handleBackToDate}
                iconSrc={null}
                style={{
                  borderRadius: "6px",
                }}
                backgroundColor={themeMode === "dark" ? "#302d4c" : "#e9e8ea"}
                color={currentTheme.callhistory.text}
              />
            )}
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <Button
              label="Cancel"
              onClick={handleClose}
              style={{
                borderRadius: "6px",
              }}
            />

            <Button
              label="Schedule Call"
              onClick={handleScheduleCall}
              disabled={!selectedSlot}
              style={{
                borderRadius: "6px",
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
